import type { Question, Blueprint } from "@shared/schema";

// Rank for each cognitive level, low → high. Used to order an MCQ test as a
// difficulty ramp: Knowledge first, then Understanding, then Higher-Order
// Thinking.
const COGNITIVE_LEVEL_ORDER: Record<Question["level"], number> = {
  "Knowledge": 1,
  "Understanding": 2,
  "Higher-Order Thinking": 3,
};

// Sort questions globally by cognitive level (Knowledge → Understanding →
// Higher-Order Thinking), ignoring topic grouping. Stable: questions at the
// same level keep their existing relative order. Returns a new array; the
// input is not mutated.
export function sortQuestionsByCognitiveLevel(questions: Question[]): Question[] {
  return questions
    .map((question, index) => ({ question, index }))
    .sort((a, b) => {
      const diff =
        COGNITIVE_LEVEL_ORDER[a.question.level] - COGNITIVE_LEVEL_ORDER[b.question.level];
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map((x) => x.question);
}

// Shuffle all questions. Returns a new array; input is not mutated.
export function randomizeQuestions(questions: Question[]): Question[] {
  return [...questions].sort(() => Math.random() - 0.5);
}

// Group questions by topic (alphabetical by topic name). Returns a new array.
export function sortQuestionsByTopic(
  questions: Question[],
  blueprint: Blueprint | null | undefined,
): Question[] {
  const topicName = (q: Question) =>
    blueprint?.topics.find((t) => t.id === q.topicId)?.name || "";
  return [...questions].sort((a, b) => topicName(a).localeCompare(topicName(b)));
}

// Keep the cognitive-level bands in order (Knowledge → Understanding →
// Higher-Order Thinking) but shuffle the questions within each band.
export function randomizeWithinDifficulty(questions: Question[]): Question[] {
  const byLevel: Record<Question["level"], Question[]> = {
    "Knowledge": [],
    "Understanding": [],
    "Higher-Order Thinking": [],
  };
  questions.forEach((q) => byLevel[q.level].push(q));
  const out: Question[] = [];
  (["Knowledge", "Understanding", "Higher-Order Thinking"] as Question["level"][]).forEach(
    (level) => {
      out.push(...byLevel[level].sort(() => Math.random() - 0.5));
    },
  );
  return out;
}

// Keep topics in blueprint order but shuffle the questions within each topic.
export function randomizeWithinTopic(
  questions: Question[],
  blueprint: Blueprint | null | undefined,
): Question[] {
  const byTopic: Record<string, Question[]> = {};
  questions.forEach((q) => {
    (byTopic[q.topicId] ||= []).push(q);
  });
  const out: Question[] = [];
  const seen = new Set<string>();
  blueprint?.topics.forEach((topic) => {
    seen.add(topic.id);
    const topicQuestions = byTopic[topic.id] || [];
    out.push(...topicQuestions.sort(() => Math.random() - 0.5));
  });
  // Append any questions whose topicId isn't in the blueprint (orphaned/legacy
  // data, or no blueprint at all) so reordering never silently drops items.
  Object.keys(byTopic).forEach((topicId) => {
    if (seen.has(topicId)) return;
    out.push(...byTopic[topicId].sort(() => Math.random() - 0.5));
  });
  return out;
}
